// src/pages/DashboardPage.tsx
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import '../App.css';

// Типы для удобства
interface BasicList {
    name: string;
    keywords: string[];
}

function DashboardPage() {
    // ==========================================================
    // ВСЕ ВЫЗОВЫ ХУКОВ (useState, useEffect, useMemo, useRef, useContext)
    // ДОЛЖНЫ БЫТЬ ВЫШЕ ЛЮБОЙ УСЛОВНОЙ ЛОГИКИ ИЛИ РАННЕГО ВОЗВРАТА!
    // ==========================================================
    const auth = useContext(AuthContext);
    const navigate = useNavigate();

    // State hooks
    const [sourcePhrases, setSourcePhrases] = useState<string>('');
    const [uniqueWords, setUniqueWords] = useState<string[]>([]);
    const [selectedUniqueWords, setSelectedUniqueWords] = useState<Set<string>>(new Set());
    const [selectAllUniqueWords, setSelectAllUniqueWords] = useState<boolean>(false);
    const [basicLists, setBasicLists] = useState<BasicList[]>([]);
    const [activeBasicListName, setActiveBasicListName] = useState<string>('');
    const [newBasicListName, setNewBasicListName] = useState<string>('');
    const [basicListSearchQuery, setBasicListSearchQuery] = useState<string>('');
    const [showBasicListSearchResults, setShowBasicListSearchResults] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [highlightMatchesModal, setHighlightMatchesModal] = useState<boolean>(false);
    const [matchesList, setMatchesList] = useState<string[]>([]);
    const [wordToHighlight, setWordToHighlight] = useState<string>('');

    // Ref hooks
    const basicListSearchContainerRef = useRef<HTMLDivElement>(null);
    const basicListInputRef = useRef<HTMLInputElement>(null);

    // Memoized values
    const filteredBasicLists = useMemo(() => {
        const query = basicListSearchQuery.toLowerCase().trim();
        let results = basicLists.slice().sort((a, b) => a.name.localeCompare(b.name));

        if (query) {
            results = results.filter(list =>
                list.name.toLowerCase().includes(query)
            );
        }
        return results;
    }, [basicLists, basicListSearchQuery]);

    // Effect hooks
    // ВСЕ useEffect должны быть здесь, на верхнем уровне, перед любыми условными возвратами.

    // Эффект для навигации, если пользователь не аутентифицирован.
    useEffect(() => {
        if (!auth || !auth.user) {
            navigate('/login');
        }
    }, [auth, navigate]);

    // Эффекты, связанные с localStorage и списками базовых слов.
    // Логика внутри этих эффектов теперь проверяет наличие auth.user.
    useEffect(() => {
        if (!auth || !auth.user) {
            // Если пользователь не аутентифицирован, не пытаемся получить данные из localStorage.
            // Сам хук будет вызван, но его внутренняя логика будет пропущена.
            return;
        }
        const userSpecificKey = `basicKeywordsLists_${auth.user.username}`;
        try {
            const storedLists = localStorage.getItem(userSpecificKey);
            if (storedLists) {
                const parsedLists: BasicList[] = JSON.parse(storedLists);
                setBasicLists(parsedLists);
                const storedActiveListName = localStorage.getItem(`activeBasicListName_${auth.user.username}`);
                if (storedActiveListName && parsedLists.some(list => list.name === storedActiveListName)) {
                    setActiveBasicListName(storedActiveListName);
                } else if (parsedLists.length > 0) {
                    setActiveBasicListName(parsedLists[0].name);
                }
            } else {
                const defaultList = { name: 'Default', keywords: [] };
                setBasicLists([defaultList]);
                setActiveBasicListName('Default');
            }
        } catch (error) {
            console.error("Failed to load basic lists from localStorage", error);
            const defaultList = { name: 'Default', keywords: [] };
            setBasicLists([defaultList]);
            setActiveBasicListName('Default');
        }
    }, [auth]); // Зависимость от 'auth' для реакции на статус аутентификации

    useEffect(() => {
        if (!auth || !auth.user) {
            return; // Пропускаем сохранение, если пользователь не аутентифицирован
        }
        const userSpecificKey = `basicKeywordsLists_${auth.user.username}`;
        try {
            localStorage.setItem(userSpecificKey, JSON.stringify(basicLists));
            localStorage.setItem(`activeBasicListName_${auth.user.username}`, activeBasicListName);
        } catch (error) {
            console.error("Failed to save basic lists to localStorage", error);
        }
    }, [basicLists, activeBasicListName, auth]); // Зависимость от 'auth'

    // Эффект для закрытия модального окна по Esc
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && highlightMatchesModal) {
                setHighlightMatchesModal(false);
                setMatchesList([]);
                setWordToHighlight('');
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [highlightMatchesModal]);

    // Эффект для скрытия результатов поиска списков по клику вне элемента
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (basicListSearchContainerRef.current && !basicListSearchContainerRef.current.contains(event.target as Node)) {
                setShowBasicListSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [basicListSearchContainerRef]);

    // Эффект для синхронизации чекбокса "Выбрать все"
    useEffect(() => {
        const allSelected = uniqueWords.length > 0 && selectedUniqueWords.size === uniqueWords.length;
        if (selectAllUniqueWords !== allSelected) {
            setSelectAllUniqueWords(allSelected);
        }
    }, [selectedUniqueWords, uniqueWords, selectAllUniqueWords]);


    // ==========================================================
    // ВСЕ ВЫЗОВЫ ХУКОВ ЗАВЕРШЕНЫ. ДАЛЕЕ МОЖЕТ БЫТЬ УСЛОВНАЯ ЛОГИКА И РАННИЙ ВОЗВРАТ.
    // ==========================================================

    // Условный ранний возврат *после* того, как все хуки были вызваны.
    if (!auth || !auth.user) {
        return null; // Прекращаем рендеринг компонента, если пользователь не аутентифицирован
    }

    // С этого момента 'auth' и 'auth.user' гарантированно существуют.
    const { logout, user } = auth;


    // --- Вспомогательные функции и обработчики действий остаются без изменений ---

    const showSnackbar = (msg: string) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const getActiveBasicKeywords = (): string[] => {
        const activeList = basicLists.find(list => list.name === activeBasicListName);
        return activeList ? activeList.keywords : [];
    };

    const updateActiveBasicKeywords = (newKeywords: string[]) => {
        setBasicLists(prevLists =>
            prevLists.map(list =>
                list.name === activeBasicListName
                    ? { ...list, keywords: newKeywords }
                    : list
            )
        );
    };

    const handleSplitWords = () => {
        const phrases = sourcePhrases.split('\n').filter(line => line.trim() !== '');
        if (phrases.length === 0) {
            showSnackbar('Введите исходные фразы.');
            setUniqueWords([]);
            setSelectedUniqueWords(new Set());
            setSelectAllUniqueWords(false);
            return;
        }

        let allWords: string[] = [];
        phrases.forEach(phrase => {
            String(phrase).split(/[\s,.;:!?«»()"'`~@#$%^&*+=_/\\|-]+/g)
                .forEach(w => {
                    w = w.trim().toLowerCase();
                    if (w) allWords.push(w);
                });
        });

        const dict: { [key: string]: boolean } = {};
        allWords.forEach(w => dict[w] = true);
        const uniq = Object.keys(dict).sort();

        setUniqueWords(uniq);
        setSelectedUniqueWords(new Set());
        setSelectAllUniqueWords(false);
        showSnackbar(`Уникальных слов: ${uniq.length}`);
    };

    const handleUniqueWordCheckboxChange = (word: string, isChecked: boolean) => {
        setSelectedUniqueWords(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isChecked) {
                newSelected.add(word);
            } else {
                newSelected.delete(word);
            }
            return newSelected;
        });
    };

    const handleSelectAllUniqueWordsChange = (isChecked: boolean) => {
        setSelectAllUniqueWords(isChecked);
        if (isChecked) {
            setSelectedUniqueWords(new Set(uniqueWords));
        } else {
            setSelectedUniqueWords(new Set());
        }
    };

    const findAndShowMatchesForWord = (wordToSearch: string) => {
        const word = wordToSearch.trim().toLowerCase();
        if (!word) {
            setMatchesList([]);
            showSnackbar('Слово для поиска не может быть пустым.');
            return;
        }

        const phrases = sourcePhrases.split('\n').filter(line => line.trim() !== '');
        if (phrases.length === 0) {
            showSnackbar('Нет фраз для поиска в источнике.');
            setMatchesList([]);
            setWordToHighlight(wordToSearch);
            setHighlightMatchesModal(true);
            return;
        }

        const safeWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`(^|[^\\p{L}\\p{N}])${safeWord}($|[^\\p{L}\\p{N}])`, 'iu');

        const foundMatches: string[] = [];
        phrases.forEach(phrase => {
            if (re.test(phrase)) {
                foundMatches.push(phrase);
            }
        });

        setMatchesList(foundMatches);
        setWordToHighlight(wordToSearch);
        setHighlightMatchesModal(true);

        if (foundMatches.length === 0) {
            showSnackbar(`Совпадений для "${wordToSearch}" не найдено.`);
        } else {
            showSnackbar(`Найдено совпадений: ${foundMatches.length} для "${wordToSearch}"`);
        }
    };

    const handleCopySelectedWords = async () => {
        if (selectedUniqueWords.size === 0) {
            showSnackbar('Не выбрано ни одного слова для копирования.');
            return;
        }
        const wordsToCopy = Array.from(selectedUniqueWords).join('\n');
        try {
            await navigator.clipboard.writeText(wordsToCopy);
            showSnackbar(`Скопировано ${selectedUniqueWords.size} слов.`);
        } catch (err) {
            console.error('Ошибка копирования:', err);
            showSnackbar('Ошибка копирования. Попробуйте еще раз.');
        }
    };

    const handlePurgeByBasic = () => {
        const currentUniqueWords = uniqueWords;
        const activeBasicKeywords = getActiveBasicKeywords();
        const basicWordsSet = new Set(activeBasicKeywords.map(w => w.toLowerCase()));

        if (basicWordsSet.size === 0) {
            showSnackbar('Нет активных базовых слов для очистки.');
            return;
        }
        if (currentUniqueWords.length === 0) {
            showSnackbar('Нет уникальных слов для очистки.');
            return;
        }

        let removedCount = 0;
        const filteredWords = currentUniqueWords.filter(word => {
            if (basicWordsSet.has(word.toLowerCase())) {
                removedCount++;
                return false;
            }
            return true;
        });

        setUniqueWords(filteredWords);
        setSelectedUniqueWords(new Set());
        setSelectAllUniqueWords(false);
        showSnackbar(`Удалено: ${removedCount} слов. Осталось: ${filteredWords.length}.`);
    };

    const handleMoveWordsToBasic = () => {
        const wordsToMove = Array.from(selectedUniqueWords);

        if (wordsToMove.length === 0) {
            showSnackbar('Выберите слова для переноса в Basic.');
            return;
        }
        if (!activeBasicListName) {
            showSnackbar('Выберите или создайте список базовых слов.');
            return;
        }

        const activeBasicKeywords = getActiveBasicKeywords();
        const newBasicWordsSet = new Set(activeBasicKeywords.map(w => w.toLowerCase()));

        let addedCount = 0;
        wordsToMove.forEach(word => {
            const lowerCaseWord = word.toLowerCase();
            if (!newBasicWordsSet.has(lowerCaseWord)) {
                newBasicWordsSet.add(lowerCaseWord);
                addedCount++;
            }
        });

        const updatedKeywords = Array.from(newBasicWordsSet).sort();
        updateActiveBasicKeywords(updatedKeywords);

        setUniqueWords(prevUnique => prevUnique.filter(word => !selectedUniqueWords.has(word)));
        setSelectedUniqueWords(new Set());
        setSelectAllUniqueWords(false);

        showSnackbar(`Добавлено в Basic: ${addedCount} слов.`);
    };

    const handleCreateNewList = () => {
        if (!newBasicListName.trim()) {
            showSnackbar('Введите имя для нового списка.');
            return;
        }
        const trimmedName = newBasicListName.trim();
        if (basicLists.some(list => list.name.toLowerCase() === trimmedName.toLowerCase())) {
            showSnackbar(`Список с именем "${trimmedName}" уже существует.`);
            return;
        }

        const newList: BasicList = { name: trimmedName, keywords: [] };
        setBasicLists(prevLists => [...prevLists, newList]);
        setActiveBasicListName(trimmedName);
        setBasicListSearchQuery('');
        setNewBasicListName('');
        showSnackbar(`Список "${trimmedName}" создан и активирован.`);
        setShowBasicListSearchResults(false);
    };

    const handleDeleteActiveList = () => {
        if (!activeBasicListName) {
            showSnackbar('Нет активного списка для удаления.');
            return;
        }
        if (basicLists.length <= 1) {
            showSnackbar('Невозможно удалить последний список. Создайте новый, чтобы удалить этот.');
            return;
        }

        const confirmDelete = window.confirm(`Вы уверены, что хотите удалить список "${activeBasicListName}"?`);
        if (confirmDelete) {
            const updatedLists = basicLists.filter(list => list.name !== activeBasicListName);
            setBasicLists(updatedLists);
            const newActiveListName = updatedLists.length > 0 ? updatedLists[0].name : '';
            setActiveBasicListName(newActiveListName);
            setBasicListSearchQuery('');
            showSnackbar(`Список "${activeBasicListName}" удален.`);
            setShowBasicListSearchResults(false);
        }
    };

    const handleSelectSearchedList = (listName: string) => {
        setActiveBasicListName(listName);
        setBasicListSearchQuery('');
        setShowBasicListSearchResults(false);
        basicListInputRef.current?.blur();
    };

    return (
        <div className="container">
            {/* Добавление навигации и кнопки выхода */}
            <div className="dashboard-header-controls">
                <button className="btn-small" onClick={logout}>Выйти ({user.username})</button>
                {user.role === 'admin' && (
                    <button className="btn-small" onClick={() => navigate('/admin')}>
                        <span className="icon"><svg viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99A4 4 0 0 1 16 16c0 1.1-.9 2-2 2h-4c-1.1 0-2-.9-2-2a4 4 0 0 1 4-4zm-4-4a4 4 0 0 1 4-4h.01V7H8a4 4 0 0 1 4 4z"/></svg></span>
                        Админка
                    </button>
                )}
            </div>

            <h1>Keywords Tool</h1>

            <div className="input-group">
                <label htmlFor="sourcePhrases">Исходные фразы:</label>
                <textarea
                    id="sourcePhrases"
                    value={sourcePhrases}
                    onChange={(e) => setSourcePhrases(e.target.value)}
                    rows={8}
                    placeholder="Введите фразы, каждую с новой строки..."
                ></textarea>
            </div>

            <button className="btn" onClick={handleSplitWords}>
                <span className="icon"><svg viewBox="0 0 24 24"><path d="M3 5h18v2H3V5zm0 12h18v2H3v-2zm0-6h10v2H3v-2z"/></svg></span>
                Разбить фразы (в уникальные слова)
            </button>

            <div className="input-group">
                <div className="unique-words-header">
                    <label>Уникальные слова (аналог столбца B):</label>
                    <button className="btn-icon-small" title="Копировать выбранные слова" onClick={handleCopySelectedWords} disabled={selectedUniqueWords.size === 0}>
                        <svg viewBox="0 0 24 24">
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                    </button>
                </div>

                {uniqueWords.length > 0 && (
                    <div className="select-all-checkbox-container">
                        <input
                            type="checkbox"
                            id="selectAllUniqueWords"
                            checked={selectAllUniqueWords}
                            onChange={(e) => handleSelectAllUniqueWordsChange(e.target.checked)}
                        />
                        <label htmlFor="selectAllUniqueWords">Выбрать все</label>
                    </div>
                )}
                
                <div className="unique-words-list-container scrollable">
                    {uniqueWords.length === 0 ? (
                        <p className="placeholder-text">Здесь появятся уникальные слова после разбиения.</p>
                    ) : (
                        <ul className="unique-words-list">
                            {uniqueWords.map((word, index) => (
                                <li key={index} className="unique-word-item">
                                    <input
                                        type="checkbox"
                                        id={`word-${index}`}
                                        checked={selectedUniqueWords.has(word)}
                                        onChange={(e) => handleUniqueWordCheckboxChange(word, e.target.checked)}
                                    />
                                    <label htmlFor={`word-${index}`}>{word}</label>
                                    <button
                                        className="btn-icon-small"
                                        title={`Найти совпадения для "${word}"`}
                                        onClick={() => findAndShowMatchesForWord(word)}
                                    >
                                        <svg viewBox="0 0 24 24">
                                            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                        </svg>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <button className="btn" onClick={handlePurgeByBasic}>
                <span className="icon"><svg viewBox="0 0 24 24"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1z"/></svg></span>
                Очистить уникальные по Basic
            </button>
            <button className="btn" onClick={handleMoveWordsToBasic}>
                <span className="icon"><svg viewBox="0 0 24 24">
                    <path d="M12 21l-7-7h4V3h6v11h4l-7 7z"/>
                </svg></span>
                Переместить выбранные в Basic
            </button>

            <div className="input-group">
                <label htmlFor="basicListSearch">
                    Активный список базовых слов:
                    {activeBasicListName && <span className="current-active-list">{activeBasicListName}</span>}
                    {!activeBasicListName && basicLists.length === 0 && <span className="current-active-list no-list">Нет списка</span>}
                </label>
                <div className="basic-list-controls" ref={basicListSearchContainerRef}>
                    <input
                        id="basicListSearch"
                        type="text"
                        ref={basicListInputRef}
                        value={basicListSearchQuery}
                        onChange={(e) => {
                            setBasicListSearchQuery(e.target.value);
                            setShowBasicListSearchResults(true);
                        }}
                        onFocus={() => setShowBasicListSearchResults(true)}
                        placeholder="Найти или выбрать список..."
                        className="basic-list-search-input"
                        autoComplete="off"
                    />
                    <button className="btn-small" onClick={handleDeleteActiveList} disabled={basicLists.length <= 1}>
                        Удалить текущий
                    </button>

                    {showBasicListSearchResults && (
                        <div className="basic-list-search-results">
                            <ul className="search-results-list">
                                {filteredBasicLists.length > 0 ? (
                                    filteredBasicLists.map(list => (
                                        <li
                                            key={list.name}
                                            className={`search-results-item ${list.name === activeBasicListName ? 'active' : ''}`}
                                            onClick={() => handleSelectSearchedList(list.name)}
                                        >
                                            {list.name} ({list.keywords.length})
                                        </li>
                                    ))
                                ) : (
                                    <li className="search-results-item no-results">Нет совпадений.</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="basic-list-new-controls">
                    <input
                        type="text"
                        value={newBasicListName}
                        onChange={(e) => setNewBasicListName(e.target.value)}
                        placeholder="Имя нового списка..."
                        className="new-list-name-input"
                    />
                    <button className="btn-small" onClick={handleCreateNewList}>Создать новый</button>
                </div>

                {activeBasicListName && (
                    <textarea
                        value={getActiveBasicKeywords().join('\n')}
                        onChange={(e) => updateActiveBasicKeywords(e.target.value.split('\n').map(w => w.trim()).filter(w => w !== ''))}
                        rows={8}
                        placeholder="Слова в активном списке Basic Keywords..."
                        className="basic-keywords-textarea"
                    ></textarea>
                )}
                {!activeBasicListName && basicLists.length > 0 && (
                    <p className="placeholder-text">Выберите список базовых слов, или создайте новый.</p>
                )}
                 {!activeBasicListName && basicLists.length === 0 && (
                    <p className="placeholder-text">Создайте первый список базовых слов.</p>
                )}
            </div>

            <div className="caption">Simple Negative Keywords Tool 1.5.0 (Local Version)</div>

            {message && <div id="snackbar" className="show">{message}</div>}

            {highlightMatchesModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Совпадения для "{wordToHighlight}"</h3>
                        {matchesList.length > 0 ? (
                            <ul className="matches-list">
                                {matchesList.map((match, index) => (
                                    <li key={index}>{match}</li>
                                ))}
                            </ul>
                        ) : (
                            <p>Совпадений не найдено.</p>
                        )}
                        <div className="modal-buttons">
                            <button onClick={() => {
                                setHighlightMatchesModal(false);
                                setMatchesList([]);
                                setWordToHighlight('');
                            }}>Закрыть</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DashboardPage;